import { create } from "zustand";
import type { UserPublic } from "@smsbridge/shared";
import { api, getAccessToken, setAccessToken, setOnAuthExpired } from "../lib/api";
import { toast } from "../lib/toast";

interface AuthResponse {
  user: UserPublic;
  accessToken: string;
  accessTokenExpiresInSeconds: number;
}

interface AuthState {
  user: UserPublic | null;
  /** null until the initial /auth/me probe settles — routes must wait, not bounce to /login. */
  status: "loading" | "authenticated" | "anonymous";
  login: (email: string, password: string, totpCode?: string) => Promise<void>;
  register: (input: {
    email: string;
    password: string;
    name: string;
    timezone?: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  /** Restores the session on a hard refresh using the persisted access token. */
  bootstrap: () => Promise<void>;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  status: "loading",

  login: async (email, password, totpCode) => {
    const { data } = await api.post<AuthResponse>("/auth/login", {
      email,
      password,
      ...(totpCode ? { totpCode } : {}),
    });
    setAccessToken(data.accessToken);
    set({ user: data.user, status: "authenticated" });
  },

  register: async (input) => {
    const { data } = await api.post<AuthResponse>("/auth/register", input);
    setAccessToken(data.accessToken);
    set({ user: data.user, status: "authenticated" });
  },

  logout: async () => {
    // Best-effort: even if the server call fails, the local session must still end.
    await api.post("/auth/logout", {}).catch(() => undefined);
    setAccessToken(null);
    set({ user: null, status: "anonymous" });
  },

  bootstrap: async () => {
    if (!getAccessToken()) {
      set({ user: null, status: "anonymous" });
      return;
    }
    try {
      // A stale access token 401s here, which the axios interceptor turns into a refresh
      // attempt using the httpOnly cookie — so a returning user with a live session stays
      // logged in across a browser restart without ever re-entering a password.
      const { data } = await api.get<UserPublic>("/auth/me");
      set({ user: data, status: "authenticated" });
    } catch {
      setAccessToken(null);
      set({ user: null, status: "anonymous" });
    }
  },
}));

// When a refresh ultimately fails, drop the session so ProtectedRoute redirects.
//
// This callback fires ONLY for an involuntary expiry — the interceptor in lib/api.ts calls it
// after a refresh attempt has failed. The deliberate `logout()` action above sets the same
// anonymous status but never routes through here, which is why signing out by hand stays silent.
setOnAuthExpired(() => {
  // Read before writing: a dead token typically fails several in-flight requests at once, and
  // each rejection invokes this callback. The first one flips the status; every later one sees
  // it already anonymous and returns without queueing a duplicate toast. Without this you get a
  // stack of identical messages, one per parallel request.
  const alreadyAnonymous = useAuthStore.getState().status === "anonymous";
  useAuthStore.setState({ user: null, status: "anonymous" });
  if (alreadyAnonymous) return;

  // Informational, not an error: an expiring session is expected behaviour, not a failure, and
  // a red danger toast would read as something having gone wrong.
  //
  // The stable id is a second line of defence behind the status guard — sonner replaces a toast
  // that reuses an id rather than stacking it, so even a race that slipped past the guard can
  // only ever show one.
  //
  // 6s rather than the 3.5s default: this is read AFTER a redirect, so the clock starts while
  // the login page is still painting.
  toast.info("Your session expired — please sign in again.", {
    id: "session-expired",
    duration: 6_000,
  });
});
